import {useState, useCallback, useEffect} from "react";
import {NotificationModel, UserRequests} from "@/app/api/user";
import {InvitationModel, InvitationRequests, InvitationStatusEnum} from "@/app/api/invitation";
import {OpenMatchRequests} from "@/app/api/open_match";
import { logEventSafe } from "@/app/utils/analytics";

export interface ScreenState {
  hasError?: string;
  hasLoading: boolean;
  notifications?: NotificationModel[];
  invitations?: InvitationModel[];
  groupedNotifications: {
    today: NotificationModel[];
    yesterday: NotificationModel[];
    lastSevenDays: NotificationModel[];
    lastThirtyDays: NotificationModel[];
  };
  acceptButtonLoading: { [invitationId: number]: boolean };
  declineButtonLoading: { [invitationId: number]: boolean };
  showMatchConflictWarning: boolean;
}

const useNotifications = () => {

  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    hasLoading: false,
    notifications: undefined,
    invitations: undefined,
    groupedNotifications: {
      today: [],
      yesterday: [],
      lastSevenDays: [],
      lastThirtyDays: []
    },
    acceptButtonLoading: {},
    declineButtonLoading: {},
    showMatchConflictWarning: false
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const splitNotifications = (notifications: NotificationModel[]) => {
    const now = new Date();
    const today: NotificationModel[] = [];
    const yesterday: NotificationModel[] = [];
    const lastSevenDays: NotificationModel[] = [];
    const lastThirtyDays: NotificationModel[] = [];

    notifications.forEach(n => {
      if (!n.timestamp) return;

      const date = n.timestamp instanceof Date ? n.timestamp : new Date(n.timestamp);

      const diffTime = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (date.toDateString() === now.toDateString()) {
        today.push(n);
      } else if (
        date.toDateString() === new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toDateString()
      ) {
        yesterday.push(n);
      } else if (diffDays < 7) {
        lastSevenDays.push(n);
      } else if (diffDays < 30) {
        lastThirtyDays.push(n);
      }
    });

    // Sort each group by timestamp (most recent first)
    const sortByTimestamp = (a: NotificationModel, b: NotificationModel) => {
      if (!a.timestamp || !b.timestamp) return 0;
      const dateA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
      const dateB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
      return dateB.getTime() - dateA.getTime();
    };

    today.sort(sortByTimestamp);
    yesterday.sort(sortByTimestamp);
    lastSevenDays.sort(sortByTimestamp);
    lastThirtyDays.sort(sortByTimestamp);

    return {today, yesterday, lastSevenDays, lastThirtyDays};
  }

  const fetchData = async () => {
    try {
      // Fetch notifications and invitations in parallel
      const [notifications, invitations] = await Promise.all([
        UserRequests.getUserNotifications(),
        InvitationRequests.getMyInvitations()
      ]);

      const grouped = splitNotifications(notifications);
      updateState({
        notifications: notifications,
        invitations: invitations,
        groupedNotifications: grouped,
        hasError: undefined
      });
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoading: false});
    }
  };

  useEffect(() => {
    updateState({hasLoading: true});
    
    fetchData();
  }, [updateState]);

  const acceptInvitation = useCallback(async (invitationId: number) => {
    // Set loading state for this specific button
    updateState({ 
      acceptButtonLoading: { ...state.acceptButtonLoading, [invitationId]: true } 
    });

    try {
      // Find the invitation to get the openMatchId
      const invitation = state.invitations?.find(inv => inv.id === invitationId);
      if (!invitation) {
        throw new Error('Invitation not found');
      }


      // Get current user and add them to the match
      const currentUser = await UserRequests.getCurrentUser();
      await OpenMatchRequests.addUser({
        matchId: invitation.openMatchId,
        newUserId: currentUser.user.id
      });

      // Accept the invitation
      await InvitationRequests.changeInvitationStatus({
        invitationId,
        invitationStatus: InvitationStatusEnum.ACCEPTED
      });

      // Log match accept event
      await logEventSafe('padel_match_accept', {
        match_id: invitation.openMatchId,
        club_id: invitation.courtId,
        user_skill_level: currentUser.skillLevel
      });

      // Update local state to reflect the change
      if (state.invitations) {
        const updatedInvitations = state.invitations.map(inv => 
          inv.id === invitationId 
            ? { ...inv, status: InvitationStatusEnum.ACCEPTED }
            : inv
        );
        updateState({ invitations: updatedInvitations });
      }

      await fetchData();
    } catch (error: any) {
      console.log('Accept invitation error:', error);
      
      // Check if it's a 400 error (user already has a match at this time)
      // Handle different possible error structures
      const is400Error = 
        (error.response && error.response.status === 400) ||
        (error.status === 400) ||
        (error.response && error.response.data && error.response.data.status === 400) ||
        (error.code === 400) ||
        (error.statusCode === 400);
      
      if (is400Error) {
        console.log('Detected 400 error - showing match conflict warning');
        updateState({ showMatchConflictWarning: true });
      } else {
        updateState({ hasError: error.message });
      }
    } finally {
      // Clear loading state for this button
      const newAcceptButtonLoading = { ...state.acceptButtonLoading };
      delete newAcceptButtonLoading[invitationId];
      updateState({ acceptButtonLoading: newAcceptButtonLoading });
    }
  }, [state.invitations, state.acceptButtonLoading, updateState]);

  const declineInvitation = useCallback(async (invitationId: number) => {
    // Set loading state for this specific button
    updateState({ 
      declineButtonLoading: { ...state.declineButtonLoading, [invitationId]: true } 
    });

    try {
      await InvitationRequests.changeInvitationStatus({
        invitationId,
        invitationStatus: InvitationStatusEnum.DECLINED
      });

      // Log match decline event
      const invitation = state.invitations?.find(inv => inv.id === invitationId);
      if (invitation) {
        await logEventSafe('padel_match_decline', {
          match_id: invitation.openMatchId
        });
      }
      
      // Update local state to reflect the change
      if (state.invitations) {
        const updatedInvitations = state.invitations.map(inv => 
          inv.id === invitationId 
            ? { ...inv, status: InvitationStatusEnum.DECLINED }
            : inv
        );
        updateState({ invitations: updatedInvitations });
      }
    } catch (error: any) {
      updateState({ hasError: error.message });
    } finally {
      // Clear loading state for this button
      const newDeclineButtonLoading = { ...state.declineButtonLoading };
      delete newDeclineButtonLoading[invitationId];
      updateState({ declineButtonLoading: newDeclineButtonLoading });
    }
  }, [state.invitations, state.declineButtonLoading, updateState]);

  const getInvitationById = useCallback((invitationId: number) => {
    return state.invitations?.find(inv => inv.id === invitationId);
  }, [state.invitations]);

  const toggleMatchConflictWarning = useCallback(() => {
    updateState({ showMatchConflictWarning: !state.showMatchConflictWarning });
  }, [state.showMatchConflictWarning, updateState]);

  return {
    state,
    acceptInvitation,
    declineInvitation,
    getInvitationById,
    toggleMatchConflictWarning,
  };
};

export default useNotifications;